import React from "react";
import { DisabledPrompt, Link, useRoute, useWorkflowStep, useBCMSContext, useParams, BCMSRecord, BCMSDate, BCMSDefaultModel } from "@bcm-dev/bcms-vite";
import { getWorkflowSteps, getDefaultTravelPlan } from "components/workflows/AddTravelPlan";
import Classes, { Trip, TravelPlan, Destination } from "model";

import MainContentContainer, { MainContentHeader, MainContentSection } from "components/shared/containers/MainContentContainer";
import { UICancelButton, UIPrimaryButton } from "components/ui/UIButton";
import DestinationSelect from "components/ui/DestinationSelect";
import FormContainer, { FormRow, FormButtonContainer } from "components/shared/containers/FormContainer";
import UIDatePicker from "components/ui/UIDatePicker ";

import "./styles.css";
/**
 * @function AddTravelPlanPage
 * @return {React.Component}
 * @exported true
 * @description The main component for AddTravelPlanPage
 */

export default function AddTravelPlanPage(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor([ TravelPlan, Trip ], globalController);
  const defaultTravelPlan = BCMSDefaultModel.travelPlan();
  const defaultTrip = BCMSDefaultModel.trip();
  // Initalize the workflow step
  const workflowStep = useWorkflowStep(getDefaultTravelPlan, getWorkflowSteps);
  const { trip, plan } = workflowStep.getStepState;
  // Functions to update the plan and trip properties
  const updatePlan = prop => value => workflowStep.update([ "plan", prop ], value);
  const updateDestinations = value => {
    const { destinations } = value;
    const name = destinations
      .filter(item => item.selected === true, [])
      .reduce((result, item) => `${ result }${ item.name }, `, "");

    workflowStep.update([ "trip", "destinationName" ], name.slice(0, name.length - 2));
  }

  const cardID = useParams().cardID ?? "0";
  const numOfDays = props.numOfDays ?? "90";

  const navigate = useRoute();
  const goBack = evt => navigate(-3);
  // Create the trip and travel plan and commit it to model, then route back
  const submit = evt => {
    // If the destination is not set, use the destination from the default trip in the model.
    var _trip = trip;
    _trip = BCMSDefaultModel.mergeRecords(_trip, defaultTrip);
    // If the return date is equal to the current date, get the return date of the default travel plan.
    plan.returnDate = plan.returnDate.isEqual(BCMSDate.init())
      ? defaultTravelPlan.returnDate
      : plan.returnDate;
    // Set the flag icon if the destination is a single country that has an icon supported
    _trip.flagIcon = _trip.destinationName.indexOf(",") === -1
      ? Destination.getFlagByName(_trip.destinationName)
      : "";

    BCMSRecord.batch([
      Trip.create(_trip),
      TravelPlan.create(plan)
    ], globalController, Classes);

    goBack();
  };

  return (
    <MainContentContainer className="add-travel-plan-page" header={
      <MainContentHeader text="Add Travel Plan" onClick={ goBack } />
    }>
      <FormContainer>
        <MainContentSection text="Traveling to" className="travel-to-section">
          <FormRow>
            <DestinationSelect onChange={ updateDestinations } />
          </FormRow>
          <p>You can add up to 15 domestic or international destinations to a Travel Plan.</p>
        </MainContentSection>
        <MainContentSection text="Travel Dates" className="travel-dates-section">
          <FormRow>
            <UIDatePicker dateLabel="Date of Departure*" onChange={ updatePlan("departDate") } />
          </FormRow>

          <FormRow>
            <UIDatePicker dateLabel="Date of Return*" onChange={ updatePlan("returnDate") } />
          </FormRow>

          <FormRow className="text-container">
            <p>Your Travel Plan cannot exceed {numOfDays} days.</p>
          </FormRow>
          <FormRow className="contact-us-row text-container">
            <p>For any issues or concerns, <DisabledPrompt><Link className="contact-us">contact us</Link></DisabledPrompt></p>
          </FormRow>
          <FormButtonContainer>
            {/* submit button should open modal */}
            <UIPrimaryButton text="Submit" onClick={ submit } />
            <UICancelButton text="Cancel" onClick={ goBack } />
          </FormButtonContainer>
        </MainContentSection>
      </FormContainer>
    </MainContentContainer>
  );
}